import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyword, SmartAction } from '@connect-x-app/shared-domain-models';
import { Response } from '@connect-x-app/web-state';
import { createColumnHelper, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';
import { isEmpty } from 'lodash';

import { TableCell, TableHeader } from '../../../../../shared';

type Props = {
  form: Response<SmartAction, SmartAction>;
  renderCheckbox?: boolean;
};

export const useKeywordTable = ({ form, renderCheckbox = true }: Props) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<Keyword>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const data = useMemo(() => {
    return [...(form.values?.keywords ?? [])].sort((a, b) => a.keyword.localeCompare(b.keyword));
  }, [form.values?.keywords]);

  const columns = [
    columnHelper.accessor('keyword', {
      cell: props => <TableCell {...props} withCheckbox={renderCheckbox} />,
      header: props => (
        <TableHeader {...props} title={t('SMART_ACTION_FORM_KEYWORDS_TABLE_HEADER')} withCheckbox={renderCheckbox} />
      ),
      id: 'KEYWORDS',
      size: 10,
    }),
    columnHelper.accessor('description', {
      cell: props => <TableCell {...props} />,
      header: props => <TableHeader {...props} title={t('SMART_ACTION_FORM_KEYWORDS_TABLE_DESCRIPTION_HEADER')} />,
      id: 'Description',
    }),
  ];

  const table = useReactTable({
    columns,
    data,
    enableRowSelection: renderCheckbox,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.keyword,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  return {
    deleteKeyword: () => {
      const selectedKeywords = Object.keys(rowSelection);
      const updatedKeywords = (form.values.keywords ?? []).filter(item => !selectedKeywords.includes(item.keyword));
      form.setAttribute(updatedKeywords, 'keywords');
      table.resetRowSelection();
    },
    isEmpty: isEmpty(form.values?.keywords),
    selectedKeywords: Object.keys(rowSelection),
    table,
  };
};
